import {AnchorProvider, BN, Program, web3} from "@coral-xyz/anchor";
import {PublicKey, SYSVAR_RENT_PUBKEY, Transaction, SystemProgram, Keypair, Signer, ComputeBudgetProgram, Connection } from "@solana/web3.js";
import {Buffer} from "buffer";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountInstruction,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID
} from "@solana/spl-token";
const COMMITMENT = "confirmed";
const {
  AnchorUtils,
  ON_DEMAND_DEVNET_PID,
  Randomness,
  Queue,
  asV0Tx,
  getDefaultDevnetQueue,
  getDefaultQueue,
  getProgramId
} = require("@switchboard-xyz/on-demand");
import starRaffleDevIdl from '../../web3/idl/star_raffle_dev.json';
import starRaffleIdl from '../../web3/idl/star_raffle_prod.json';
import * as CONSTANTS from "../../constants/constants";
import {getEnvironment} from "../../helpers/helper";
import {TBA_UNIX_DATE} from "../../constants/constants";
import moment from "moment/moment";
import {ON_DEMAND_DEVNET_QUEUE} from "@switchboard-xyz/on-demand";
import {ON_DEMAND_MAINNET_QUEUE} from "@switchboard-xyz/on-demand";

const PROGRAM_NAME = "star_raffle"
const devProgramId = 'GhHchyTXjaVf8Y2Y3xqHkLgSiKhwbR6uL8LdfRnhSR46'
const prodProgramId = '9Vn8Pgkz1sXjeM6kpUdCZDBANKWzxv9EP9QSUNfMbQn4';

export const getRaffleProgramData = (provider) => {
  const idl = getEnvironment() === CONSTANTS.PRODUCTION ? starRaffleIdl : starRaffleDevIdl;

  const starRaffleProgram = new Program(idl, provider);

  return {
    program: starRaffleProgram,
    programName: PROGRAM_NAME,
    programId: getEnvironment() === CONSTANTS.PRODUCTION ? new PublicKey(prodProgramId) : new PublicKey(devProgramId),
  }
}
export const checkIfRaffleUser = async ({ provider, connection }) => {

  const {
    program,
    programName,
    programId
} = getRaffleProgramData(provider);

  const [raffleUser, raffleUserBump] = PublicKey.findProgramAddressSync(
    [
      provider.wallet.publicKey.toBuffer(),
      Buffer.from(programName),
      Buffer.from("raffle_user")
    ],
    programId
  );

  const raffleInit = await connection.getBalance(raffleUser);
  console.log(raffleInit)
  return raffleInit > 0;
}

export const getRaffleManagers = async ({provider, connection, raffleName}) => {
  try {
    const { program, programId } = getRaffleProgramData(provider);

    const [raffle] = PublicKey.findProgramAddressSync(
      [Buffer.from(raffleName)],
      programId
    );

    const raffleAccount = await program.account.raffle.fetch(raffle);
    
    return {
      primaryManager: raffleAccount.manager.toString(),
      additionalManagers: raffleAccount.managers.map(manager => manager.toString())
    };
  } catch (err) {
    console.error('Failed to get raffle managers:', err);
    throw err;
  }
};

export const addRaffleManager = async ({provider, connection, raffleName, newManagerAddress}) => {
  try {
    const { program, programId } = getRaffleProgramData(provider);

    const [raffle] = PublicKey.findProgramAddressSync(
      [Buffer.from(raffleName)],
      programId
    );

    const ix = await program.methods
      .addManager(new PublicKey(newManagerAddress))
      .accounts({
        owner: provider.wallet.publicKey,
        raffle,
      })
      .instruction();

    const transaction = new Transaction();
    const { blockhash } = await connection.getLatestBlockhash();
    
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = provider.wallet.publicKey;
    transaction.add(ix);

    return await provider.sendAndConfirm(transaction);

  } catch (err) {
    console.error('Failed to add manager:', err);
    throw err;
  }
};

export const removeRaffleManager = async ({provider, connection, raffleName, managerAddress}) => {
  try {
    const { program, programId } = getRaffleProgramData(provider);

    const [raffle] = PublicKey.findProgramAddressSync(
      [Buffer.from(raffleName)],
      programId
    );

    const ix = await program.methods
      .removeManager(new PublicKey(managerAddress))
      .accounts({
        owner: provider.wallet.publicKey,
        raffle,
      })
      .instruction();

    const transaction = new Transaction();
    const { blockhash } = await connection.getLatestBlockhash();
    
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = provider.wallet.publicKey;
    transaction.add(ix);

    return await provider.sendAndConfirm(transaction);

  } catch (err) {
    console.error('Failed to remove manager:', err);
    throw err;
  }
};

export const isRaffleManager = async ({provider, connection, raffleName, address}) => {
  try {
    const { program, programId } = getRaffleProgramData(provider);

    const [raffle] = PublicKey.findProgramAddressSync(
      [Buffer.from(raffleName)],
      programId
    );

    const raffleAccount = await program.account.raffle.fetch(raffle);
    const addressPubkey = new PublicKey(address);

    return (
      raffleAccount.primary_manager.equals(addressPubkey) || 
      raffleAccount.managers.some(m => m.equals(addressPubkey))
    );
  } catch (err) {
    console.error('Failed to check manager status:', err);
    return false;
  }
};


export const createRaffleUser = async ({ provider, connection }) => {
  const {
    program,
    programName,
    programId
  } = getRaffleProgramData(provider);

  const [mainData, mainDataBump] = await PublicKey.findProgramAddress(
    [Buffer.from(programName)],
    programId
  );
  const [raffleUser, raffleUserBump] = await PublicKey.findProgramAddress(
    [
      provider.wallet.publicKey.toBuffer(),
      Buffer.from(programName),
      Buffer.from("raffle_user")
    ],
    programId
  );
  const transaction = new Transaction();

  transaction.add(
    program.instruction.createUser(
      raffleUserBump,
      {
        accounts: {
          owner: provider.wallet.publicKey,
          userAuthority: provider.wallet.publicKey,
          raffleUser: raffleUser,
          mainData: mainData,
          systemProgram: SystemProgram.programId,
          rent: SYSVAR_RENT_PUBKEY,
        }
      }
    ));

  const signature = await provider.wallet.sendTransaction(transaction, connection);

  return await connection.confirmTransaction(signature, 'processed');
}

export const createRaffle = async ({ connection, provider, raffleData }) => {
  try {
    const { program, programId } = getRaffleProgramData(provider);

    const [raffle, raffleBump] = PublicKey.findProgramAddressSync(
        [Buffer.from(raffleData.raffleName)],
        programId
    );

    const [raffleUser] = PublicKey.findProgramAddressSync(
        [
          provider.wallet.publicKey.toBuffer(),
          Buffer.from("star_raffle"),
          Buffer.from("raffle_user")
        ],
        programId
    );

    const [raffleVault] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(raffleData.raffleName),
          Buffer.from("raffle_vault")
        ],
        programId
    );

    console.log(raffleData)
    const winnerTiers = [
      {
        tierPosition: 1,
        prizePercentage: 5000, // 50.00%
        fixedAmount: new BN(10000000),
        maxWinners: 1 // 2 winners for 1st place
      },
     /* {
        tierPosition: 2,
        prizePercentage: 3000, // 30.00%
        fixedAmount: new anchor.BN(0),
        maxWinners: 2 // 3 winners for 2nd place
      },
      {
        tierPosition: 3,
        prizePercentage: 1500, // 15.00%
        fixedAmount: new anchor.BN(0),
        maxWinners: 3 // 5 winners for 3rd place
      }*/
    ];


    const accounts = {
      owner: provider.wallet.publicKey,
      raffle,
      raffleUser,
      currency: new PublicKey(raffleData.tokenMint),
      raffleVault,
      prizeVault: null,
      systemProgram: SystemProgram.programId,
      tokenProgram: TOKEN_PROGRAM_ID,
      rent: SYSVAR_RENT_PUBKEY,
    };

    if (raffleData.prizeCurrency) {
      accounts.prizeCurrency = raffleData.prizeCurrency;
    }

    const ix = await program.methods
        .createRaffle(
            raffleBump,
            raffleData.raffleName,
            new BN(raffleData.maxTicketRaffle),
            new BN(raffleData.maxWinners),
            new BN(raffleData.contributionStartTime),
            new BN(raffleData.contributionEndTime),
            new PublicKey(raffleData.tokenMint),
            new BN(raffleData.ticketPrice),
            new BN(raffleData.maxTicketUser),
            winnerTiers
        )
        .accounts(accounts).rpc();


    return ix;

  } catch (err) {
    console.error('Failed to create raffle:', err);
    if (err.logs) {
      console.error('Program logs:', err.logs);
    }
    throw err;
  }
};

export const updateRaffle = async ({ connection, provider, raffleData }) => {
  try {
    const { program, programId } = getRaffleProgramData(provider);

    const [raffle] = PublicKey.findProgramAddressSync(
        [Buffer.from(raffleData.raffleName)],
        programId
    );
    const [raffleUser] = PublicKey.findProgramAddressSync(
        [
          provider.wallet.publicKey.toBuffer(),
          Buffer.from("star_raffle"),
          Buffer.from("raffle_user")
        ],
        programId
    );

    const tx = await program.methods
        .updateRaffle(
            new BN(Number(raffleData.maxTicketRaffle)),
            new BN(Number(raffleData.maxWinners)),
            new BN(raffleData.contributionStartTime),
            new BN(raffleData.contributionEndTime),
            new PublicKey(raffleData.tokenMint),
            new BN(Number(raffleData.ticketPrice)),
            new BN(Number(raffleData.maxTicketUser))
        )
        .accounts({
          owner: provider.wallet.publicKey,
          raffle,
          raffleUser,
          currency: new PublicKey(raffleData.tokenMint),
          systemProgram: SystemProgram.programId,
          tokenProgram: TOKEN_PROGRAM_ID,
          rent: SYSVAR_RENT_PUBKEY,
        })
        .rpc();

    return tx;
  } catch (err) {
    console.error('Failed to update raffle:', err);
    throw err;
  }
};

export const pickWinner = async ({ provider, connection, raffleName }) => {

  try{
    const {
      program,
      programId,
      programName
    } = getRaffleProgramData(provider);

    const userAuthority = provider.wallet.publicKey;
    
    const [raffle, raffleBump] = await PublicKey.findProgramAddress(
        [Buffer.from(raffleName)],
        programId
    );
    const [participant, participantBump] = await PublicKey.findProgramAddress(
        [
          provider.wallet.publicKey.toBuffer(),
          Buffer.from(raffleName),
          Buffer.from("raffle_participant")
        ],
        programId
    );

    const sbProgramId = await getProgramId(connection);
    // 1. Get Switchboard program
    const sbProvider = new AnchorProvider(
        connection,
        provider.wallet,
        { commitment: COMMITMENT }
    );
    const rngKp = Keypair.generate();
    const sbIdl = await Program.fetchIdl(sbProgramId, sbProvider);
    const sbProgram = new Program(sbIdl, sbProvider);
    const [randomness, ix] = await Randomness.create(sbProgram, rngKp, ON_DEMAND_MAINNET_QUEUE);

    console.log("Randomness account:", randomness.pubkey.toString());



    // Use regular transaction instead of V0
    const createRandomnessTx = new Transaction();
    createRandomnessTx.add(ix);
    createRandomnessTx.feePayer = userAuthority;
    createRandomnessTx.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;
    createRandomnessTx.sign(rngKp)

    const txOpts = {
      commitment: COMMITMENT,
      skipPreflight: true,  // Skip preflight to avoid LUT slot validation issue
      maxRetries: 3,
    };
    console.log(createRandomnessTx);
    const sig1 = await provider.wallet.sendTransaction(createRandomnessTx, connection, txOpts);
    const latestBlockhash = await connection.getLatestBlockhash();
    await connection.confirmTransaction({
      signature: sig1,
      blockhash: latestBlockhash.blockhash,
      lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
    }, COMMITMENT);
    console.log("Transaction signature for randomness account creation:", sig1);

    // Wait a bit for the account to be fully available
    console.log("Waiting for randomness account to be available...");
    await new Promise(resolve => setTimeout(resolve, 15000));

    // Verify account exists using raw connection with explicit commitment
    const accountInfo = await connection.getAccountInfo(rngKp.publicKey, COMMITMENT);
    if (!accountInfo) {
      console.log("Account not found, retrying with finalized commitment...");
      const accountInfo2 = await connection.getAccountInfo(rngKp.publicKey, "finalized");
      if (!accountInfo2) {
        throw new Error(`Randomness account ${rngKp.publicKey.toString()} was not created`);
      }
      console.log("Randomness account verified (finalized), owner:", accountInfo2.owner.toString());
    } else {
      console.log("Randomness account verified, owner:", accountInfo.owner.toString());
    }

    // Recreate sbProgram with fresh provider to avoid caching issues
    const sbProvider2 = new AnchorProvider(
        connection,
        provider.wallet,
        { commitment: COMMITMENT }
    );
    const sbIdl2 = await Program.fetchIdl(sbProgramId, sbProvider2);
    const sbProgram2 = new Program(sbIdl2, sbProvider2);

    // Reload the randomness account after it's been created  
    const reloadedRandomness = new Randomness(sbProgram2, rngKp.publicKey);

    // With your randomness account at the ready, commit to the oracle's prediction
    console.log("\nCommitting to randomness and picking winner...");
    const commitIx = await reloadedRandomness.commitIx(ON_DEMAND_MAINNET_QUEUE);

    // Create pick winner instruction
    const pickWinnerIx = await program.methods
        .pickWinner(rngKp.publicKey)
        .accounts({
          signer: userAuthority,
          raffle: raffle,
          participant: participant,
          randomnessAccountData: rngKp.publicKey,
          systemProgram: SystemProgram,
          rent: SYSVAR_RENT_PUBKEY,
        })
        .instruction();

    // Use regular transaction instead of V0
    const commitTx = new Transaction();
    commitTx.add(commitIx);
    commitTx.add(pickWinnerIx);
    commitTx.feePayer = userAuthority;
    commitTx.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;

    const sig = await provider.wallet.sendTransaction(commitTx, connection, txOpts);
    await connection.confirmTransaction(sig, COMMITMENT);
  } catch(e) {
    console.error(e);
    throw e
  }

}

export const finalizeWinner = async ({ provider, connection, raffleName }) => {

  try {
    const {
      program,
      programId,
      programName
    } = getRaffleProgramData(provider);

    const userAuthority = provider.wallet.publicKey;

    const [raffle, raffleBump] = await PublicKey.findProgramAddress(
        [Buffer.from(raffleName)],
        programId
    );

    const raffleData = await program.account.raffle.fetch(raffle);

    console.log("\nCurrent Raffle Data:");
    console.log("- Total Participants:", raffleData.totalParticipants.toString());
    console.log("- Current Index:", raffleData.currentIndex.toString());
    console.log("- Commit Slot:", raffleData.commitSlot.toString());
    console.log("- Randomness Account:", raffleData.randomnessAccount.toString());
    console.log("- Winners Selected:", raffleData.winnerIndexes.filter(w => w.ticketIndex.toString() !== "18446744073709551615").length);

    // Get current slot
    const currentSlot = await connection.getSlot();
    console.log("- Current Slot:", currentSlot);
    console.log("- Slots since commit:", currentSlot - raffleData.commitSlot.toNumber());

    // Load Switchboard program and randomness account
    const sbProgramId = await getProgramId(connection);
    const sbIdl = await Program.fetchIdl(sbProgramId, provider);
    const sbProgram = new Program(sbIdl, provider);

    console.log("\nRevealing randomness...");
    const randomness = new Randomness(sbProgram, raffleData.randomnessAccount);


    try {


      const revealTx = new Transaction();
      const revealIx = await randomness.revealIx();
      console.log("Reveal ix:", revealIx);
      revealTx.add(revealIx);
      revealTx.feePayer = userAuthority;
      revealTx.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;
      console.log("Reveal tx:", revealTx);

      const revealSig = await provider.wallet.sendTransaction(revealTx, connection, {
        skipPreflight: true,
        maxRetries: 3,
        commitment: COMMITMENT
      });
      console.log("Reveal sig:", revealSig);
      await connection.confirmTransaction(revealSig, COMMITMENT);
      console.log("Randomness revealed:", revealSig);
    } catch (error) {
      console.error("Reveal may have already been called or isn't needed yet:", error);
    }

    // Wait for the Switchboard oracle network to settle the randomness
    // On-Demand oracles need time to respond and populate the random value
    console.log("\nWaiting for Switchboard oracles to settle the randomness...");
    console.log("This may take 30-60 seconds as oracles respond...");
    await new Promise(resolve => setTimeout(resolve, 45000));

    // Finalize winner instruction
    console.log("\nFinalizing winner...");
    const tx = await program.methods
        .finalizeWinner()
        .accounts({
          signer: userAuthority,
          raffle: raffle,
          randomnessAccountData: raffleData.randomnessAccount,
          systemProgram: SystemProgram,
        })
        .rpc();

    console.log("\nWinner Finalized! Transaction signature:", tx);

    // Get updated raffle data
    const updatedRaffle = await program.account.raffle.fetch(raffle);
    console.log("\nUpdated Raffle Data:");
    console.log("- Winners Selected:", updatedRaffle.winnerIndexes.filter(w => w.ticketIndex.toString() !== "18446744073709551615").length);

    // Show winner information
    const winners = updatedRaffle.winnerIndexes
        .filter(w => w.ticketIndex.toString() !== "18446744073709551615")
        .map((w, i) => ({
          index: i + 1,
          ticketNumber: w.ticketIndex.toString(),
          claimed: w.claimed
        }));

    console.log("\nWinning Tickets:");
    winners.forEach(w => {
      console.log(`Winner ${w.index}:`);
      console.log(`- Ticket Number: ${w.ticketNumber}`);
      console.log(`- Claimed: ${w.claimed}`);
    });

    console.log("\nTo claim a prize:");
    console.log("1. Check if your ticket ranges contain any of the winning ticket numbers");
    console.log("2. Use the claim_prize.js script with your winning ticket number");

  } catch (err) {
    console.error('Failed to pick winner:', err);
    throw err;
  }

}

export const getRaffleData = async ({ raffleName, provider }) => {
  const {
    program,
    programName,
    programId
  } = getRaffleProgramData(provider);

  const [raffle, raffleBump] = await PublicKey.findProgramAddress(
      [
        Buffer.from(raffleName)
      ],
      programId
  );
  return await program.account.raffle.fetch(raffle);
}

export const getRaffleWinners = async ({ raffleName, provider, connection}) => {
  try {
    const {
      program,
      programId,
      programName
    } = getRaffleProgramData(provider);

    const userAuthority = provider.wallet.publicKey;

    const [raffle, raffleBump] = await PublicKey.findProgramAddress(
        [Buffer.from(raffleName)],
        programId
    );

    const raffleData = await program.account.raffle.fetch(raffle);

    const maxWinners = raffleData.maxWinners.toNumber();

    console.log("\n=== RAFFLE DATA ===");
    console.log("Raffle PDA:", raffle.toString());
    console.log("Max Winners:", maxWinners);
    console.log("Total Tickets Sold:", raffleData.currentIndex.toString());
    console.log("Total Participants:", raffleData.totalParticipants.toString());

    // Get winning ticket numbers
    const realWinners = raffleData.winnerIndexes.filter(w =>
        w.ticketIndex.toString() !== "18446744073709551615"
    );
    const winningTickets = realWinners.map(w => w.ticketIndex.toNumber());

    console.log(`\n=== WINNING TICKETS: ${realWinners.length}/${maxWinners} ===`);
    winningTickets.forEach((ticket, i) => {
      const claimed = realWinners[i].claimed;
      console.log(`  Ticket #${ticket} - ${claimed ? '✓ CLAIMED' : '✗ Unclaimed'}`);
    });

    // Get all participant accounts for this raffle
    console.log("\n=== FETCHING ALL PARTICIPANTS ===");

    // Fetch all accounts owned by the program
    const accounts = await connection.getProgramAccounts(programId);
    console.log(`Found ${accounts.length} total program accounts`);

    // Decode and filter for participant accounts belonging to this raffle
    const participants = [];
    for (const { pubkey, account } of accounts) {
      try {
        // Try to decode as Participant
        const decoded = program.coder.accounts.decode('participant', account.data);
        if (decoded.raffle.toString() === raffle.toString()) {
          participants.push({
            publicKey: pubkey,
            account: decoded
          });
        }
      } catch (e) {
        // Not a participant account or decode failed, skip
        continue;
      }
    }

    console.log(`Found ${participants.length} participants for this raffle\n`);

    let winnerCount = 0;
    let winners = [];

    for (const participant of participants) {
      const authority = participant.account.userWallet.toString();
      const bought = participant.account.bought.toNumber();
      const ticketRanges = participant.account.ticketRanges;
      const usdcSpent = participant.account.usdcSpent.toString();
      const hydrazineSpent = participant.account.hydrazineSpent.toString();

      // Check if this participant has any winning tickets
      const participantWinningTickets = [];
      for (const winningTicket of winningTickets) {
        for (const range of ticketRanges) {
          const start = range.start.toNumber();
          const end = range.end.toNumber();
          if (winningTicket >= start && winningTicket < end) {
            participantWinningTickets.push(winningTicket);
          }
        }
      }

      const isWinner = participantWinningTickets.length > 0;
      if (isWinner) winnerCount++;


      if (isWinner) {
        winners.push({
          wallet: authority,
          bought,
          ticketRanges,
          winningTickets: participantWinningTickets
        });
        console.log(`  🎉 WINNING TICKETS: ${participantWinningTickets.join(', ')}`);
        // Check claim status for each winning ticket
        participantWinningTickets.forEach(ticket => {
          const winnerIndex = winningTickets.indexOf(ticket);
          const claimed = realWinners[winnerIndex].claimed;
          console.log(`     Ticket #${ticket}: ${claimed ? '✓ CLAIMED' : '✗ Not claimed yet'}`);
        });
      }
    }

    // Summary
    console.log("\n=== SUMMARY ===");
    console.log(`Total Participants: ${participants.length}`);
    console.log(`Total Winners: ${winnerCount}`);
    console.log(`Winning Tickets Selected: ${realWinners.length}/${maxWinners}`);
    console.log(`Total Tickets Sold: ${raffleData.currentIndex.toString()}`);
    console.log(winners)
   return winners

  } catch (error) {
    console.error("Error:", error.message);
    if (error.logs) {
      console.error("Program Logs:", error.logs);
    }
  }
}

export const fundPrizePool = async ({ provider, connection, raffleName, prizeTokenMint, amount }) => {
  try {
    const { program, programId } = getRaffleProgramData(provider);

    const [raffle, raffleBump] = PublicKey.findProgramAddressSync(
        [Buffer.from(raffleName)],
        programId
    );

    const [raffleVault] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(raffleName),
          Buffer.from("raffle_vault")
        ],
        programId
    );

    const [prizeVault] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(raffleName),
          Buffer.from("prize_vault")
        ],
        programId
    );

    const raffleData = await program.account.raffle.fetch(raffle);

    const managerWallet = provider.wallet.publicKey;

    // Check if the wallet is a manager
    const primaryManagerPubkey = new PublicKey(raffleData.manager);
    const isPrimaryManager = primaryManagerPubkey.equals(managerWallet);
    const isAdditionalManager = raffleData.managers && raffleData.managers.some(mgr => {
      const mgrPubkey = new PublicKey(mgr);
      return mgrPubkey.equals(managerWallet);
    });
    const isManager = isPrimaryManager || isAdditionalManager;

    if (!isManager) {
      console.error("❌ Error: This wallet is not authorized as a manager for this raffle.");
      console.error("Primary Manager:", raffleData.manager.toString());
      if (raffleData.managers && raffleData.managers.length > 0) {
        console.error("Additional Managers:");
        raffleData.managers.forEach((mgr, i) => {
          console.error(`  ${i + 1}. ${mgr.toString()}`);
        });
      }
      return;
    }

    console.log("✅ Manager authorization verified");
    console.log("");

    // Determine prize currency
    const purchaseCurrency = new PublicKey(raffleData.currency);
    const prizeCurrency = raffleData.prizeCurrency
        ? new PublicKey(raffleData.prizeCurrency)
        : purchaseCurrency;
    const isSameCurrency = purchaseCurrency.equals(prizeCurrency);

    console.log("📋 Currency Information:");
    console.log("- Purchase Currency:", purchaseCurrency.toString());
    console.log("- Prize Currency:", prizeCurrency.toString());
    console.log("- Same Currency:", isSameCurrency ? "Yes" : "No");
    console.log("");

    // Validate prize token mint matches prize currency
    if (!prizeTokenMint.equals(prizeCurrency)) {
      console.error("❌ Error: Prize token mint does not match raffle's prize currency");
      console.error("Expected:", prizeCurrency.toString());
      console.error("Provided:", prizeTokenMint.toString());
      return;
    }

    // Get manager's token account
    const managerTokenAccount = await getAssociatedTokenAddress(
        prizeTokenMint,
        managerWallet
    );

    console.log("💳 Manager Token Account:", managerTokenAccount.toString());

    // Check if token account exists, create if needed
    const tokenAccountInfo = await connection.getAccountInfo(managerTokenAccount, COMMITMENT);
    if (!tokenAccountInfo) {
      console.log("📝 Creating associated token account...");
      const createATAInstruction = createAssociatedTokenAccountInstruction(
          managerWallet,      // payer
          managerTokenAccount, // associatedToken
          managerWallet,      // owner
          prizeTokenMint,     // mint
          TOKEN_PROGRAM_ID,  // programId
          ASSOCIATED_TOKEN_PROGRAM_ID // associatedTokenProgramId
      );

      const createTx = new Transaction();
      createTx.add(createATAInstruction);
      createTx.feePayer = managerWallet;
      createTx.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;

      const createSig = await provider.wallet.sendTransaction(createTx, connection, {
        skipPreflight: true,
        maxRetries: 3,
        commitment: COMMITMENT
      });
      await connection.confirmTransaction(createSig, COMMITMENT);
      console.log("✅ Token account created! Signature:", createSig);
      console.log("");
    } else {
      console.log("✅ Token account already exists");
      console.log("");
    }

    // Check manager's token balance
    const managerTokenAccountInfo = await connection.getParsedAccountInfo(managerTokenAccount);
    let managerBalance = "0";
    if (managerTokenAccountInfo.value && managerTokenAccountInfo.value.data && managerTokenAccountInfo.value.data.parsed) {
      managerBalance = managerTokenAccountInfo.value.data.parsed.info.tokenAmount.amount;
    }

    if (BigInt(managerBalance) < amount) {
      console.error("❌ Error: Insufficient balance in manager token account");
      console.error("Required:", amount.toString());
      console.error("Available:", managerBalance);
      return;
    }

    console.log("💵 Manager Token Balance:", managerBalance);
    console.log("");

    // Fund prize pool
    console.log("🚀 Executing prize pool funding transaction...");
    const accounts = {
      manager: managerWallet,
      raffle: raffle,
      managerTokenAccount: managerTokenAccount,
      prizeCurrencyMint: prizeTokenMint, // Prize currency mint account (required for token account initialization)
      prizeVault: prizeVault, // Always include - will be initialized if needed
      raffleVault: raffleVault, // Always include
      tokenProgram: TOKEN_PROGRAM_ID,
      systemProgram: SystemProgram,
      rent: SYSVAR_RENT_PUBKEY,
    };

    // Log which vault will be used for funding
    if (isSameCurrency) {
      console.log("📦 Funding raffle vault (same currency)");
    } else {
      console.log("📦 Funding prize vault (different currency - will be initialized if needed)");
    }
    console.log("");

    const tx = await program.methods
        .fundPrizePool(new BN(amount.toString()))
        .accounts(accounts)
        .rpc();

    console.log("");
    console.log("=".repeat(50));
    console.log("✅ Prize Pool Funding Successful!");
    console.log("=".repeat(50));
    console.log("Transaction Signature:", tx);
    console.log("Funding Amount:", amount.toString());
    console.log("Prize Currency:", prizeCurrency.toString());
    console.log("Vault Used:", isSameCurrency ? "Raffle Vault" : "Prize Vault");
    console.log("=".repeat(50));

    // Fetch updated raffle data to show new prize pool amount
    const updatedRaffleData = await program.account.raffle.fetch(raffle);
    console.log("");
    console.log("📊 Updated Prize Pool Amount:", updatedRaffleData.prizePoolAmount.toString());

  } catch (error) {
    console.error("❌ Error:", error.message);
    if (error.logs) {
      console.error("Program Logs:", error.logs);
    }
    console.error("\nStack trace:", error.stack);
  }
}

export const hasRaffleEnded = (raffleInfo) => {
  const timeNow = moment().unix();

  const startTimeTBA = TBA_UNIX_DATE > raffleInfo.contributionStartTime;
  const endTimeTBA = TBA_UNIX_DATE > raffleInfo.contributionEndTime;

  if (startTimeTBA || endTimeTBA) {
    return false;
  }

  if (raffleInfo.contributionEndTime < timeNow) {
    return true;
  }

  return false;
}

export const isRaffleOngoing = (raffleInfo) => {
  if (!raffleInfo) return false;

  const timeNow = moment().unix();

  return (raffleInfo.contributionStartTime < timeNow && !hasRaffleEnded(raffleInfo));
}



export const solanaDefaultToken = {
  label: 'Wrapped SOL',
  name: 'Solana',
  key: 'SOL',
  value: 'So11111111111111111111111111111111111111112',
  icon: 'https://img.raydium.io/icon/So11111111111111111111111111111111111111112.png',
  decimals: 6,
}

export const starsDefaultToken = {
  label: 'StarLaunch',
  name: 'Starlaunch',
  key: 'STARS',
  value: 'HCgybxq5Upy8Mccihrp7EsmwwFqYZtrHrsmsKwtGXLgW',
  icon: 'https://img.raydium.io/icon/HCgybxq5Upy8Mccihrp7EsmwwFqYZtrHrsmsKwtGXLgW.png',
  decimals: 6,
}

export const usdcDefaultToken = {
  label: 'USD Coin',
  name: 'USD Coin',
  key: 'USDC',
  value: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
  icon: 'https://img.raydium.io/icon/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v.png',
  decimals: 6,
}


export const hydrazineDefaultToken = {
  "value": "4q5UBXJxE91BZKX548qhU8i5QBWvZdXzS3RZwfTgLQda",
  "decimals": 6,
  "extensions": {},
  "icon": "https://img.raydium.io/icon/4q5UBXJxE91BZKX548qhU8i5QBWvZdXzS3RZwfTgLQda.png",
  "label": "HYDRAZINE",
  "name": "HYDRAZINE",
  "key": "N2H4"
}
export const NO_TOKENS_RESPONSE = 'NO_TOKENS_FOUND';
